import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Cell, PieChart, Pie, Legend, Line, ComposedChart
} from 'recharts';
import moment from 'moment';
import '../farmDashboard.css';
import FlierTargeting from '../components/FlierTargeting';
import { stagger, onEnter, onLeave } from '../lib/stagger';
import { ModalShell, PageHeader } from '../lib/ui';

// ---------------- Constants ----------------
const API_BASE  = 'https://teardrophydro.co.uk/garageFarm/stats';
const FARM_BASE = 'https://teardrophydro.co.uk/garageFarm';

// ---------------- Helpers ----------------
const rateColor = rate => rate >= 60 ? '#22c55e' : rate >= 30 ? '#f59e0b' : '#ef4444';

// Flier QR codes can be scanned as either "flier" or "leaflet" sources/paths
const isFlierPath = (path) => /\/api\/r\/(flier|leaflet)/i.test(path || '');
const isFlierSource = (id) => /flier|leaflet/i.test(id || '');

// Centralised axios instance with 401 → logout
const statsApi = axios.create();
statsApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
statsApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('accessToken');
      if (window.location.pathname !== '/login') window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);


// ---------------- Sub-Components ----------------

const EVENT_LABELS = {
  page_view: 'Viewed page',
  initial_landing: 'Landed',
  home_scroll_depth: 'Scrolled page',
  add_to_cart: 'Added to cart',
  box_add_to_cart: 'Added box to cart',
  cart_view: 'Viewed cart',
  checkout_initiated: 'Started checkout',
  checkout_cash_placed: 'Order placed',
  checkout_redirect: 'To payment',
  browse_category_change: 'Changed category',
  contact_form_start: 'Started form',
  contact_form_submit_success: 'Form submitted',
  contact_form_submit_error: 'Form error',
  newsletter_view: 'Newsletter popup',
  home_hero_cta_click: 'Clicked CTA',
  home_category_click: 'Category click',
  home_taster_bag_add_to_cart: 'Added taster bag',
  go_to_cart_from_toast: 'Went to cart',
  about_cta_click: 'Clicked CTA',
  hiw_cta_click: 'Clicked CTA',
  home_subscribe_banner_cta_click: 'Subscribe click',
  home_benefits_cta_click: 'Clicked CTA',
  contact_hero_cta_click: 'Clicked CTA',
  home_testimonial_dot_click: 'Testimonial nav',
  contact_form_field_focus: 'Filled field',
  home_hiw_link_click: 'Clicked link',
  home_benefit_visible: 'Benefit visible',
  about_view: 'Viewed page',
  hiw_view: 'Viewed page',
  contact_view: 'Viewed page',
  remove_from_cart: 'Removed from cart',
  cart_quantity_change: 'Changed quantity',
  outbound_link_click: 'Clicked outbound link',
  js_error: 'JS error',
  js_unhandled_rejection: 'JS error (promise)',

  // Browse / product pages
  browse_scroll_depth: 'Scrolled page',
  browse_product_click: 'Clicked product',
  product_view: 'Viewed product',
  product_image_view: 'Viewed photo',
  product_gallery_thumb_click: 'Browsed gallery',
  product_pack_select: 'Chose pack size',
  product_load_error: 'Product load error',
  delivery_countdown_view: 'Saw delivery countdown',
  rec_add_to_cart: 'Added recommended item',

  // Homepage sections + cookie banner
  home_section_view: 'Viewed section',
  cookie_banner_shown: 'Saw cookie banner',
  cookie_banner_decision: 'Cookie choice',
  cookie_banner_settings_open: 'Opened cookie settings',

  // Subscriber site (subscribe.teardrophydro.co.uk)
  sub_hero_cta_click: 'Clicked CTA',
  sub_hiw_click: 'Clicked link',
  sub_box_subscribe_click: 'Subscribe click',
  sub_login_success: 'Logged in',
  sub_register_success: 'Registered',
  sub_new_subscription_view: 'Started subscription',
  sub_subscription_type_select: 'Chose box type',
  sub_frequency_select: 'Chose frequency',
  sub_subscription_submitted: 'Submitted subscription',
  sub_paused: 'Paused subscription',
  sub_resumed: 'Resumed subscription',
  sub_cancelled: 'Cancelled subscription',
};

// Pause/cancel come first so they don't fall through to the generic
// click/select colours below.
const SUB_EVENT_COLORS = { sub_paused: '#4f46e5', sub_resumed: '#16a34a', sub_cancelled: '#dc2626' };

const getEventColor = (event) => {
  if (!event) return '#4f7ea3';
  if (SUB_EVENT_COLORS[event]) return SUB_EVENT_COLORS[event];
  if (event === 'remove_from_cart' || event === 'cart_quantity_change') return '#ef4444';
  if (event.includes('error')) return '#dc2626';
  if (event === 'outbound_link_click') return '#8b5cf6';
  if (event.includes('add_to_cart') || event.includes('taster_bag') || event.includes('box_add') || event === 'cart_view') return '#22c55e';
  if (event.includes('checkout') || event.includes('order')) return '#f59e0b';
  if (event.includes('scroll')) return '#a855f7';
  if (event.includes('form') || event.includes('contact')) return '#f97316';
  if (event.includes('click') || event.includes('cta') || event.includes('select')) return '#14b8a6';
  if (event === 'initial_landing') return '#0ea5e9';
  return '#4f7ea3';
};

const formatDuration = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

// Collapses consecutive same-event/same-path steps (e.g. a run of
// home_scroll_depth ticks, or flicking back and forth on one product page)
// into a single node with a ×N count, using the last occurrence's
// timestamp. Keeps the journey illustrative rather than one 170px node per
// raw analytics event.
const collapsePathway = (pathway) => {
  const out = [];
  for (const step of pathway) {
    const prev = out[out.length - 1];
    if (prev && prev.event === step.event && prev.path === step.path) {
      prev.ts = step.ts;
      prev.count += 1;
      if (step.detail) prev.detail = step.detail;
    } else {
      out.push({ ...step, count: 1 });
    }
  }
  return out;
};

// Renders one session's pathway as a horizontally-scrolling node timeline.
// Shared by the main journeys list and the per-visitor history modal so both
// look and behave identically.
const JourneyTimeline = ({ pathway: rawPathway, sessionStart }) => (
  <div className="h-timeline-scroll" style={{ display: 'flex', alignItems: 'flex-start', overflowX: 'auto', paddingBottom: '20px', paddingTop: '20px' }}>
    {collapsePathway(rawPathway).map((step, idx, pathway) => {
      const isLast = idx === pathway.length - 1;
      const stepTime = moment(step.ts);
      const diffSeconds = stepTime.diff(sessionStart, 'seconds');
      const timeOffset = formatDuration(diffSeconds);

      const nextStep = pathway[idx + 1];
      const timeSpentSecs = nextStep ? moment(nextStep.ts).diff(stepTime, 'seconds') : null;
      const timeSpentLabel = timeSpentSecs !== null ? formatDuration(timeSpentSecs) : null;

      const eventLabel = EVENT_LABELS[step.event] || step.event?.replace(/_/g, ' ') || '';
      const eventColor = getEventColor(step.event);

      let cleanPath = step.path.split('?')[0].replace('/', '') || 'Home';
      if (step.path.includes('salads')) cleanPath = '🥗 Salads';
      if (step.site?.startsWith('subscribe.')) cleanPath = `Subs: ${cleanPath}`;

      const nodeIsFlier = isFlierPath(step.path);
      const nodeIsSocial = step.path.includes('/api/r/facebook');

      return (
        <div key={idx} className="h-step" style={{ display: 'flex', alignItems: 'center', minWidth: '120px', position: 'relative' }}>
          <div className="h-node-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <div
              className={`v-dot ${nodeIsFlier ? 'scan' : ''} ${isLast ? 'exit' : ''}`}
              style={{
                backgroundColor: nodeIsSocial ? '#1877F2' : (!nodeIsFlier && !isLast ? eventColor : undefined)
              }}
            />
            {!isLast && <div className="h-line-horizontal" style={{ width: '100%', left: '50%' }} />}
            <div className="h-label" style={{ marginTop: '8px', textAlign: 'center', width: '108px' }}>
              {eventLabel && (
                <div style={{ fontSize: '0.58rem', color: eventColor, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                  {eventLabel}{step.count > 1 && ` ×${step.count}`}
                </div>
              )}
              <div style={{ fontSize: '0.65rem', color: 'var(--text)', fontWeight: '600' }}>
                {cleanPath}
              </div>
              {step.detail && (
                <div style={{ fontSize: '0.6rem', color: 'var(--muted)', fontStyle: 'italic', marginTop: '1px' }}>
                  {step.detail}
                </div>
              )}
              <div style={{ fontSize: '0.58rem', color: 'var(--muted)', marginTop: '3px' }}>
                {timeSpentLabel ? `for ${timeSpentLabel}` : `+${timeOffset}`}
              </div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

// One visitor's session history - their last journey and the date of it,
// plus recent ones before that - opened from the "History" button on a
// returning-visitor badge.
const VisitorHistoryModal = ({ vid, label, onClose }) => {
  const [sessions, setSessions] = useState(null); // null = loading
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await statsApi.get(`${API_BASE}/visitor-journeys?vid=${encodeURIComponent(vid)}`);
        if (!alive) return;
        setSessions(res.data?.journeys || []);
        setCustomer(res.data?.customer || null);
      } catch {
        if (alive) setError('Could not load this visitor\'s history.');
      }
    })();
    return () => { alive = false; };
  }, [vid]);

  return (
    <ModalShell onClose={onClose} width={720} ariaLabel="Visitor history">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0 }}>
            {customer ? customer.name : `Visitor ${label}`} - recent journeys
          </h3>
          {customer && (
            <p className="dim" style={{ margin: '2px 0 0', fontSize: '0.75rem' }}>
              {customer.matchedBy === 'account' ? 'Logged in to the subscriber site' : 'Matched to a completed order'} · {customer.email} · ID {label}
            </p>
          )}
          {!customer && sessions !== null && (
            <p className="dim" style={{ margin: '2px 0 0', fontSize: '0.75rem' }}>
              No match yet for this visitor - only card checkouts and subscriber logins can be matched.
            </p>
          )}
        </div>
        <button className="btn-tab" onClick={onClose}>Close</button>
      </div>

      {error && <p className="dim">{error}</p>}
      {!error && sessions === null && <p className="dim">Loading…</p>}
      {!error && sessions && sessions.length === 0 && <p className="dim">No other sessions found for this visitor.</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {sessions?.map((s, i) => {
          const sessionStart = moment(s.startTime);
          const lastTs = s.pathway[s.pathway.length - 1]?.ts;
          const duration = lastTs ? moment.duration(moment(lastTs).diff(sessionStart)).humanize() : null;
          return (
            <div key={i} className="h-journey-row">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>
                  {i === 0 ? 'Last visit · ' : ''}{sessionStart.format('ddd D MMM YYYY, HH:mm')}
                </span>
                <span className="dim" style={{ fontSize: '0.7rem' }}>
                  {sessionStart.fromNow()}{duration ? ` · ⏱️ ${duration}` : ''}
                </span>
              </div>
              <JourneyTimeline pathway={s.pathway} sessionStart={sessionStart} />
            </div>
          );
        })}
      </div>
    </ModalShell>
  );
};

const UniversalJourneyPanel = ({ days, refreshedKey }) => {
  const [journeys, setJourneys] = useState([]);
  const [filter, setFilter] = useState('all');
  const [historyTarget, setHistoryTarget] = useState(null); // { vid, label } | null

  useEffect(() => {
    const fetchJourneys = async () => {
      const res = await statsApi.get(`${API_BASE}/universal-journeys?days=${days}`);
      setJourneys(res.data);
    };
    fetchJourneys();
  }, [days, refreshedKey]);

  const filteredJourneys = journeys.filter(j => {
    const sessionStart = moment(j.startTime);
    const lastTs = j.pathway[j.pathway.length - 1]?.ts;
    const durationSeconds = lastTs ? moment(lastTs).diff(sessionStart, 'seconds') : 0;
    
    if (j.pathway.length <= 1 || durationSeconds < 2) return false;

    const isFlier = j.pathway.some(p => isFlierPath(p.path));
    if (filter === 'flier') return isFlier;
    if (filter === 'direct') return !isFlier;
    return true;
  });

  return (
    <div className="chart-container col-span-2" style={{ height: 500, overflow: 'auto' }}>
      <div className="panel-header-inline" style={{ marginBottom: '20px', justifyContent: 'space-between' }}>
        <h3>Visitor journeys</h3>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button className={`btn-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All Traffic</button>
        <button className={`btn-tab ${filter === 'flier' ? 'active' : ''}`} onClick={() => setFilter('flier')}>Flier Only</button>
        <button className={`btn-tab ${filter === 'direct' ? 'active' : ''}`} onClick={() => setFilter('direct')}>Direct/Web Only</button>
      </div>
      
      <div className="journey-list-vertical" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {filteredJourneys.map((j, i) => {
          const sessionStart = moment(j.startTime);
          const shortId = j._id?.ip?.split('.').pop() || '???';
          // Stable per-person ID (from the vid cookie) so the same visitor can be
          // matched across separate rows/sessions - unlike the IP suffix, this
          // doesn't change when someone switches wifi/mobile data mid-week.
          const visitorId = (j.vid && j.vid !== 'anonymous') ? j.vid.replace(/^v_/, '').slice(-6) : null;
          
          // 1. CALCULATE LOGIC FIRST
          const pathwayPaths = j.pathway.map(p => p.path);
          const isFlierScan = pathwayPaths.some(isFlierPath);
          const isSocialScan = pathwayPaths.some(path => path.includes('/api/r/facebook') || path.includes('/api/r/instagram'));
          const isGoogle = pathwayPaths.some(path => path.includes('google'));
          // Paid clicks land with gclid/gad_source in the URL, not "google".
          const isGoogleAd = pathwayPaths.some(path => /[?&](gclid|gad_source)=/.test(path || ''));
          
          const lastTs = j.pathway[j.pathway.length - 1].ts;
          const totalDuration = lastTs ? moment.duration(moment(lastTs).diff(sessionStart)).humanize() : null;

          // 2. DEFINE BADGE
          let sourceBadge;
          if (isFlierScan) {
            sourceBadge = <span className="source-badge flier">📍 Flier Scan</span>;
          } else if (isSocialScan) {
            sourceBadge = <span className="source-badge social" style={{backgroundColor: '#1877F2', color: 'white', padding: '2px 8px', borderRadius: 'var(--r-sm)', fontSize: '0.7rem'}}>Facebook Ad</span>;
          } else if (isGoogleAd) {
            sourceBadge = <span className="source-badge google" style={{backgroundColor: '#4285F4', color: 'white', padding: '2px 8px', borderRadius: 'var(--r-sm)', fontSize: '0.7rem'}}>Google Ad</span>;
          } else if (isGoogle) {
            sourceBadge = <span className="source-badge google" style={{backgroundColor: '#4285F4', color: 'white', padding: '2px 8px', borderRadius: 'var(--r-sm)', fontSize: '0.7rem'}}>🔍 Google Ref</span>;
          } else {
            sourceBadge = <span className="source-badge web">🌐 Direct Web</span>;
          }

          let rowHighlightClass = '';
          if (isFlierScan) {
            rowHighlightClass = 'flier-path';
          } else if (isSocialScan) {
            rowHighlightClass = 'social-path'; // New class for Social
          }

          return (
            <div key={i} className={`h-journey-row ${rowHighlightClass}`} style={stagger(i)}>
              <div className="h-journey-meta" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {/* vid is only present once a visitor has accepted analytics cookies -
                      before that every session is "anonymous" and genuinely can't be
                      told apart from any other, so we say so rather than guessing "new". */}
                  {visitorId ? (
                    <>
                      <span
                        className={`source-badge ${j.isReturning ? 'returning' : 'new-visitor'}`}
                        title={j.customer ? `${j.customer.name} · ${j.customer.email} · IP ends .${shortId}` : `Visitor ID ${visitorId} · IP ends .${shortId}`}
                      >
                        {j.customer
                          ? `${j.isReturning ? '🔁' : '🆕'} ${j.customer.name}`
                          : (j.isReturning ? `🔁 Returning · ${visitorId}` : `🆕 New · ${visitorId}`)}
                      </span>
                      {j.isReturning && (
                        <button
                          className="btn-tab"
                          style={{ fontSize: '0.65rem', padding: '2px 8px' }}
                          onClick={() => setHistoryTarget({ vid: j.vid, label: visitorId })}
                        >
                          🕓 History
                        </button>
                      )}
                    </>
                  ) : (
                    <span className="source-badge unidentified" title={`No cookie consent yet · IP ends .${shortId}`}>
                      ❔ No cookie ID
                    </span>
                  )}
                  {sourceBadge}
                  <span className="dim" style={{fontSize: '0.7rem'}}>{sessionStart.fromNow()}</span>
                </div>
                {totalDuration && <div className="dim" style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>⏱️ {totalDuration} session</div>}
              </div>

              <JourneyTimeline pathway={j.pathway} sessionStart={sessionStart} />
            </div>
          );
        })}
      </div>

      {historyTarget && (
        <VisitorHistoryModal
          vid={historyTarget.vid}
          label={historyTarget.label}
          onClose={() => setHistoryTarget(null)}
        />
      )}
    </div>
  );
};

const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [activePage, setActivePage] = useState('dashboard');
  const [productSort, setProductSort] = useState('views');

  // Flier log state
  const [flierLog, setFlierLog] = useState([]);
  const [flierForm, setFlierForm] = useState({ date: '', flierType: '', numberDelivered: '' });
  const [flierFormOpen, setFlierFormOpen] = useState(false);
  const [flierSaving, setFlierSaving] = useState(false);
  const [flierMsg, setFlierMsg] = useState('');

  const fetchFlierLog = useCallback(async () => {
    try {
      const res = await statsApi.get(`${FARM_BASE}/stats/flier-log`);
      setFlierLog(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error('Failed to fetch flier log:', e); }
  }, []);

  const addFlierEntry = async (e) => {
    e.preventDefault();
    const num = Number(flierForm.numberDelivered);
    if (!flierForm.date || !flierForm.flierType || !Number.isFinite(num) || num <= 0) return;
    setFlierSaving(true);
    try {
      await statsApi.post(`${FARM_BASE}/stats/flier-log`, {
        date: flierForm.date,
        flierType: flierForm.flierType,
        numberDelivered: num,
      });
      setFlierForm({ date: '', flierType: '', numberDelivered: '' });
      setFlierFormOpen(false);
      setFlierMsg('Entry saved.');
      await fetchFlierLog();
    } catch (e) {
      setFlierMsg('Failed to save entry.');
    } finally {
      setFlierSaving(false);
      setTimeout(() => setFlierMsg(''), 3000);
    }
  };

  const deleteFlierEntry = async (id) => {
    if (!window.confirm('Remove this flier entry?')) return;
    try {
      await statsApi.delete(`${FARM_BASE}/stats/flier-log/${id}`);
      setFlierLog(l => l.filter(e => e._id !== id));
    } catch { setFlierMsg('Failed to delete entry.'); }
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await statsApi.get(`${API_BASE}/summary?days=${days}`);
      setData(res.data);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error("Fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  const fetchInsights = useCallback(async () => {
    try {
      const res = await statsApi.get(`${API_BASE}/insights?days=${days}`);
      setInsights(res.data);
    } catch (err) {
      console.error("Insights fetch failed:", err);
    }
  }, [days]);

  // Initial load + auto-refresh
  useEffect(() => {
    fetchData();
    fetchInsights();
    fetchFlierLog();
    const interval = setInterval(() => { fetchData(); fetchInsights(); }, 30000);
    return () => clearInterval(interval);
  }, [fetchData, fetchInsights, fetchFlierLog]);

  const { summary = {}, daily_traffic = [], loyalty = [], qr_sources = [], top_pages = [] } = data || {};

const flierTypes = useMemo(() => {
  return [...new Set(flierLog.map(f => f.flierType))];
}, [flierLog]);

const flierByDateAndType = useMemo(() => {
  const result = {};
  flierLog.forEach(entry => {
    const dateKey = moment(entry.date).format('YYYY-MM-DD');
    if (!result[dateKey]) result[dateKey] = {};
    if (!result[dateKey][entry.flierType]) result[dateKey][entry.flierType] = 0;
    result[dateKey][entry.flierType] += entry.numberDelivered;
  });
  return result;
}, [flierLog]);

const qrSources = useMemo(() => {
  const set = new Set();

  (data?.daily_qr_sources || []).forEach(entry => {
    if (entry.source) {
      set.add(entry.source);
    }
  });

  return Array.from(set);
}, [data]);

  const compositionData = [
    { name: 'New', value: summary.new_visitors || 0, fill: '#0ea5e9' },
    { name: 'Returning', value: summary.returning_visitors || 0, fill: '#4f7ea3' }
  ];

  const totalHits = daily_traffic.reduce((s, d) => s + (d.hits || 0), 0);
  const totalFlierScans = qr_sources
    .filter(s => isFlierSource(s._id))
    .reduce((s, q) => s + q.count, 0);
  const totalVisitors = (summary.new_visitors || 0) + (summary.returning_visitors || 0);
  const returnRate = totalVisitors > 0
    ? Math.round((summary.returning_visitors || 0) / totalVisitors * 100)
    : null;

  const loyaltyLabels = { 1: '1 Session', 2: '2-4 Sessions', 5: '5-9 Sessions', '10+': '10+ Sessions' };

const dailyOrdersMap = useMemo(() => {
  const map = {};
  (data?.daily_orders || []).forEach(d => { map[d._id] = d.orders; });
  return map;
}, [data]);

const flierVisitsByDateAndType = useMemo(() => {
  const result = {};


  (data?.daily_qr_sources || []).forEach(entry => {
    const dateKey = entry.date; // already YYYY-MM-DD

    if (!result[dateKey]) result[dateKey] = {};

    result[dateKey][entry.source] = entry.count;
  });

  return result;
}, [data]);

const mergedTraffic = useMemo(() => {
  if (!daily_traffic || daily_traffic.length === 0) return [];

  return daily_traffic.map(d => {
    const key = moment(d._id).format('YYYY-MM-DD');

    const deliveredData = flierByDateAndType[key] || {};
    const visitData = flierVisitsByDateAndType[key] || {};

    const visitFields = {};
    const deliveredFields = {};

    // Delivered only for actual fliers
    flierTypes.forEach(type => {
      deliveredFields[`${type}_delivered`] = deliveredData[type] || 0;
    });

    // Visits for ALL QR sources
    qrSources.forEach(source => {
      let count = visitData[source] || 0;      
      visitFields[`${source}_visits`] = count;
    });

    return {
      ...d,
      ...deliveredFields,
      ...visitFields,
      orders: dailyOrdersMap[key] || 0,
    };
  });
}, [
  daily_traffic,
  flierByDateAndType,
  flierVisitsByDateAndType,
  flierTypes,
  qrSources,
  dailyOrdersMap,
]);

const activeFlierTypes = useMemo(() => {
  return flierTypes.filter(type =>
    mergedTraffic.some(d => (d[`${type}_delivered`] || 0) > 0)
  );
}, [flierTypes, mergedTraffic]);

  if (loading && !data) return <div className="loading-container"><div className="spinner"></div></div>;

  return (
    <div className="dashboard-container">
      <PageHeader
        title="Insights"
        subtitle="Website traffic, fliers and what visitors do"
        tabs={[{ key: 'dashboard', label: 'Analytics' }, { key: 'targeting', label: 'Flier targeting' }]}
        activeTab={activePage}
        onTabChange={setActivePage}
      />
        {activePage === 'targeting' ? (
      <FlierTargeting />
    ) : (
      <>
      <div className="utility-strip">
        <div className="button-group">
          {[14, 30, 90].map((v, i) => (
            <button key={v} onClick={() => setDays(v)} className={days === v ? 'active':''} style={stagger(i)}>
              {v === 1 ? 'Live' : `${v}d`}
            </button>
          ))}
        </div>
        <div className="live-badge">
          <span className="pulse-dot" />
          <span>Live · {moment(lastRefreshed).format('HH:mm:ss')}</span>
        </div>
      </div>

      <div className="kpi-row">
        {[
          { label: 'Total Hits', value: totalHits.toLocaleString() },
          { label: 'Unique Visitors', value: totalVisitors.toLocaleString() },
          { label: 'Flier Scans', value: totalFlierScans.toLocaleString() },
          { label: 'Return Rate', value: returnRate !== null ? `${returnRate}%` : '-' },
        ].map(({ label, value }, i) => (
          <div key={label} className="kpi-card" style={stagger(i)}>
            <div className="kpi-value">{value}</div>
            <div className="kpi-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid-container analytics-grid" style={stagger(2)}>

        {/* Traffic + Campaign merged */}
        <div className="chart-container col-span-3" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline" style={{ justifyContent: 'space-between' }}>
            <h3>Site Activity &amp; Campaign Performance</h3>
            <button
              onClick={() => setFlierFormOpen(o => !o)}
              style={{ fontSize: 12, padding: '4px 10px', margin: 0 }}
            >
              {flierFormOpen ? 'Cancel' : '+ Add Delivery'}
            </button>
          </div>

          {flierMsg && (
            <div style={{ padding: '6px 10px', marginBottom: 8, borderRadius: 'var(--r-lg)', fontSize: 12, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}>
              {flierMsg}
            </div>
          )}

          {flierFormOpen && (
            <form onSubmit={addFlierEntry} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12, padding: '10px 12px', background: 'var(--bg)', borderRadius: 'var(--r-xl)', border: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 3 }}>Date</div>
                <input type="date" value={flierForm.date} onChange={e => setFlierForm(f => ({ ...f, date: e.target.value }))}
                  style={{ padding: '6px 8px', borderRadius: 'var(--r-md)', border: '1px solid var(--border)', fontSize: 13, background: 'var(--panel)', color: 'var(--text)' }} required />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 3 }}>Flier Type</div>
                <input type="text" placeholder="e.g. Flier4" value={flierForm.flierType}
                  onChange={e => setFlierForm(f => ({ ...f, flierType: e.target.value }))}
                  style={{ padding: '6px 8px', borderRadius: 'var(--r-md)', border: '1px solid var(--border)', fontSize: 13, width: 110, background: 'var(--panel)', color: 'var(--text)' }} required />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 3 }}>Delivered</div>
                <input type="number" min="1" placeholder="e.g. 250" value={flierForm.numberDelivered}
                  onChange={e => setFlierForm(f => ({ ...f, numberDelivered: e.target.value }))}
                  style={{ padding: '6px 8px', borderRadius: 'var(--r-md)', border: '1px solid var(--border)', fontSize: 13, width: 90, background: 'var(--panel)', color: 'var(--text)' }} required />
              </div>
              <button type="submit" disabled={flierSaving} style={{ margin: 0, padding: '6px 14px', fontSize: 13 }}>
                {flierSaving ? 'Saving…' : 'Save'}
              </button>
            </form>
          )}

          {flierLog.length > 0 && (
            <details style={{ marginBottom: 10 }}>
              <summary style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer', userSelect: 'none' }}>
                View / manage {flierLog.length} delivery entries
              </summary>
              <div style={{ marginTop: 6, maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)' }}>
                      <th style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 700 }}>Date</th>
                      <th style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 700 }}>Type</th>
                      <th style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700 }}>Delivered</th>
                      <th style={{ padding: '4px 8px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {flierLog.map(e => (
                      <tr key={e._id} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '3px 8px' }}>{e.date}</td>
                        <td style={{ padding: '3px 8px' }}>{e.flierType}</td>
                        <td style={{ padding: '3px 8px', textAlign: 'right' }}>{e.numberDelivered}</td>
                        <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                          <button onClick={() => deleteFlierEntry(e._id)} style={{ margin: 0, padding: '1px 6px', fontSize: 11, background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 'var(--r-md)' }}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          <ResponsiveContainer height={300} width="100%">
            <ComposedChart data={mergedTraffic}>
              <defs>
                <linearGradient id="journeyGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#a855f7" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#a855f7" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
              <XAxis dataKey="_id" tickFormatter={t => moment(t).format('MMM DD')} fontSize={11} />
              <YAxis yAxisId="delivered" fontSize={11} stroke="var(--muted)" />
              <YAxis yAxisId="traffic" orientation="right" fontSize={11} stroke="#f59e0b" />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />

              {activeFlierTypes.map((type, index) => {
                const colours = ['#22c55e', '#ef4444', '#3b82f6', '#a855f7', '#f97316'];
                return (
                  <Line
                    key={`${type}-delivered`}
                    yAxisId="delivered"
                    type="monotone"
                    dataKey={`${type}_delivered`}
                    name={`${type} delivered`}
                    stroke={colours[index % colours.length]}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    dot={false}
                  />
                );
              })}

              <Area
                yAxisId="traffic"
                name="Valid Journeys"
                type="monotone"
                dataKey="validJourneys"
                stroke="#a855f7"
                strokeWidth={2}
                fill="url(#journeyGrad)"
                dot={false}
              />
              <Line
                yAxisId="traffic"
                name="Unique Visitors"
                type="monotone"
                dataKey="uniques"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="traffic"
                name="Orders"
                type="monotone"
                dataKey="orders"
                stroke="#22c55e"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* 2. User Journey Tracker: Full width to show depth */}
      <UniversalJourneyPanel days={days} refreshedKey={lastRefreshed} />

        {/* 3. Composition & Loyalty (Side by Side) */}
        <div className="chart-container" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline"><h3>Visitor Type</h3></div>
          <ResponsiveContainer height={220} width="100%">
            <PieChart>
              <Pie data={compositionData} innerRadius={70} outerRadius={90} paddingAngle={2} dataKey="value">
                {compositionData.map((entry, index) => <Cell key={index} fill={entry.fill} />)}
              </Pie>
              <Tooltip />
              <Legend iconType="circle" />
            </PieChart>
          </ResponsiveContainer>
          <div className="panel-header-inline" style={{ marginTop: 12 }}><h3>Device Type</h3></div>
          {insights?.devices?.length > 0 ? (() => {
            const deviceColors = { desktop: '#4f7ea3', mobile: '#0ea5e9', tablet: '#a855f7' };
            const total = insights.devices.reduce((s, d) => s + d.count, 0);
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 6 }}>
                {insights.devices.map(({ device, count }) => {
                  const pct = total > 0 ? Math.round(count / total * 100) : 0;
                  const color = deviceColors[device] || '#94a3b8';
                  return (
                    <div key={device}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{device}</span>
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{pct}% <span style={{ color: 'var(--muted)', fontSize: 11 }}>({count})</span></span>
                      </div>
                      <div style={{ height: 8, background: 'var(--bg)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 'var(--r-md)', transition: 'width 0.6s ease' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })() : (
            <div style={{ color: 'var(--muted)', fontSize: 12, paddingTop: 6 }}>Loading…</div>
          )}
        </div>

        <div className="chart-container" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline"><h3>Visit Frequency</h3></div>
          <ResponsiveContainer height={350} width="100%">
            <BarChart data={loyalty}>
              <XAxis dataKey="_id" tickFormatter={v => loyaltyLabels[v] || v} fontSize={10} />
              <YAxis hide />
              <Tooltip cursor={{fill: 'transparent'}} />
              <Bar dataKey="count" fill="#4f7ea3" radius={[4, 4, 0, 0]}>
                {loyalty.map((entry, index) => (
                  <Cell key={`cell-${index}`} fillOpacity={0.6 + (index * 0.1)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 4. Content & QR Tables */}
        <div className="chart-container" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline"><h3>Top Content</h3></div>
          <div className="custom-table-wrapper">
            <table className="stats-table">
              <tbody>
                {top_pages.filter(p => p._id && !p._id.includes('farmStatus')).map((p, i) => (
                  <tr key={i}><td>{p._id}</td><td align="right"><b>{p.count}</b></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="chart-container" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline"><h3>QR Performance</h3></div>
          <div className="custom-table-wrapper">
            <table className="stats-table">
              <tbody>
                {qr_sources.map((s, i) => (
                  <tr key={i}><td><span className="pill">{s._id || 'Direct'}</span></td><td align="right"><b>{s.count}</b></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Conversion Funnel ── */}
        <div className="chart-container col-span-2" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline"><h3>Conversion Funnel</h3></div>
          {insights?.funnel ? (
            <div style={{ padding: '8px 0' }}>
              {insights.funnel.map((step, i) => {
                const max = insights.funnel[0]?.count || 1;
                const pct = Math.round(step.count / max * 100);
                const dropOff = i > 0 && insights.funnel[i - 1].count > 0
                  ? Math.round((1 - step.count / insights.funnel[i - 1].count) * 100)
                  : null;
                const barColor = i === 0 ? '#0ea5e9' : i === 1 ? '#f59e0b' : '#22c55e';
                return (
                  <div key={step.stage} style={{ marginBottom: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{step.stage}</span>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                        <b style={{ color: 'var(--text)' }}>{step.count}</b>
                        {dropOff !== null && (
                          <span style={{ color: '#ef4444', marginLeft: 8, fontSize: 11 }}>↓ {dropOff}% drop-off</span>
                        )}
                      </span>
                    </div>
                    <div style={{ height: 28, background: 'var(--bg)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
                      <div style={{
                        width: `${pct}%`, height: '100%',
                        background: barColor, borderRadius: 'var(--r-lg)',
                        transition: 'width 0.6s ease',
                        display: 'flex', alignItems: 'center', paddingLeft: 8,
                      }}>
                        {pct > 15 && <span style={{ fontSize: 11, color: '#fff', fontWeight: 700 }}>{pct}%</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
              <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Based on {days}d window</p>
            </div>
          ) : (
            <div style={{ color: 'var(--muted)', fontSize: 13, paddingTop: 12 }}>Loading…</div>
          )}
        </div>

        {/* ── Newsletter Popup Funnel ── */}
        <div className="chart-container" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline"><h3>Newsletter Popup</h3></div>
          {insights?.newsletter ? (() => {
            const { shown, dismissed, signups } = insights.newsletter;
            const convRate = shown > 0 ? ((signups / shown) * 100).toFixed(1) : '0.0';
            const dismissRate = shown > 0 ? Math.round(dismissed / shown * 100) : 0;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
                {[
                  { label: 'Popup shown', value: shown, color: '#0ea5e9' },
                  { label: 'Dismissed', value: dismissed, sub: `${dismissRate}% of shown`, color: '#f59e0b' },
                  { label: 'Signed up', value: signups, sub: `${convRate}% conversion`, color: '#22c55e' },
                ].map(({ label, value, sub, color }, i) => (
                  <div key={label}>
                    {i > 0 && <div style={{ fontSize: 16, color: 'var(--muted)', textAlign: 'center', marginBottom: 4 }}>↓</div>}
                    <div style={{ background: 'var(--bg)', borderRadius: 'var(--r-xl)', padding: '10px 14px', borderLeft: `3px solid ${color}` }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value.toLocaleString()}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
                      {sub && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{sub}</div>}
                    </div>
                  </div>
                ))}
              </div>
            );
          })() : (
            <div style={{ color: 'var(--muted)', fontSize: 13, paddingTop: 12 }}>Loading…</div>
          )}
        </div>

        {/* ── Product Performance ── */}
        <div className="chart-container col-span-3" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline" style={{ justifyContent: 'space-between' }}>
            <h3>Product Performance</h3>
            <div style={{ display: 'flex', gap: 6 }}>
              {['views', 'carts', 'rate'].map(k => (
                <button
                  key={k}
                  onClick={() => setProductSort(k)}
                  className={productSort === k ? 'btn-tab active' : 'btn-tab'}
                  style={{ fontSize: 11, padding: '3px 9px' }}
                >
                  {k === 'views' ? 'Most Viewed' : k === 'carts' ? 'Most Carted' : 'Conversion %'}
                </button>
              ))}
            </div>
          </div>
          {insights?.products ? (
            <div className="custom-table-wrapper">
              <table className="stats-table" style={{ tableLayout: 'auto' }}>
                <thead>
                  <tr style={{ background: 'var(--bg)' }}>
                    <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>Product</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>Views</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>Cart Adds</th>
                    <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>Conv. %</th>
                    <th style={{ padding: '6px 10px', minWidth: 120 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {[...insights.products]
                    .sort((a, b) => b[productSort] - a[productSort])
                    .map((p, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '7px 10px', fontWeight: 500, fontSize: 13 }}>{p.name}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', fontSize: 13 }}>{p.views}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', fontSize: 13 }}>{p.carts}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: rateColor(p.rate) }}>{p.rate}%</span>
                      </td>
                      <td style={{ padding: '7px 10px' }}>
                        <div style={{ height: 6, background: 'var(--bg)', borderRadius: 'var(--r-sm)', overflow: 'hidden', minWidth: 80 }}>
                          <div style={{ width: `${p.rate}%`, height: '100%', background: rateColor(p.rate), borderRadius: 'var(--r-sm)', transition: 'width 0.6s ease' }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: 'var(--muted)', fontSize: 13, paddingTop: 12 }}>Loading…</div>
          )}
        </div>

        {/* ── Hour of Day ── */}
        <div className="chart-container col-span-2" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline"><h3>Traffic by Hour</h3></div>
          {insights?.hourly ? (() => {
            const maxCount = Math.max(...insights.hourly.map(h => h.count), 1);
            const allHours = Array.from({ length: 24 }, (_, h) => {
              const found = insights.hourly.find(x => x.hour === h);
              return { hour: h, label: `${String(h).padStart(2, '0')}:00`, count: found?.count || 0 };
            });
            return (
              <ResponsiveContainer height={180} width="100%">
                <BarChart data={allHours} margin={{ top: 4, right: 0, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                  <XAxis dataKey="hour" fontSize={9} interval={5} tickFormatter={h => `${h}h`} />
                  <YAxis fontSize={10} />
                  <Tooltip
                    formatter={v => [v, 'Page views']}
                    labelFormatter={h => `${String(h).padStart(2, '0')}:00`}
                  />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                    {allHours.map((entry, i) => (
                      <Cell key={i} fill={entry.count >= maxCount * 0.75 ? '#0ea5e9' : 'var(--border)'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            );
          })() : (
            <div style={{ color: 'var(--muted)', fontSize: 13, paddingTop: 12 }}>Loading…</div>
          )}
          <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Blue bars = top 25% traffic hours</p>
        </div>

        {/* ── Category Clicks ── */}
        <div className="chart-container" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline"><h3>Category Interest</h3></div>
          {insights?.categories?.length > 0 ? (() => {
            const maxCat = Math.max(...insights.categories.map(c => c.count), 1);
            const catColors = { salads: '#22c55e', herbs: '#84cc16', floristry: '#ec4899', micro: '#a855f7' };
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
                {insights.categories.map(({ name, count }) => {
                  const pct = Math.round(count / maxCat * 100);
                  const color = catColors[name] || '#0ea5e9';
                  return (
                    <div key={name}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{name}</span>
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{count}</span>
                      </div>
                      <div style={{ height: 10, background: 'var(--bg)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 'var(--r-md)', transition: 'width 0.6s ease' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })() : (
            <div style={{ color: 'var(--muted)', fontSize: 13, paddingTop: 12 }}>
              {insights ? 'No category data yet' : 'Loading…'}
            </div>
          )}
        </div>

        {/* ── Campaign Attribution ── */}
        <div className="chart-container col-span-2" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline"><h3>Campaign Attribution</h3></div>
          {insights?.attribution ? (
            insights.attribution.length > 0 ? (
              <div className="custom-table-wrapper">
                <table className="stats-table" style={{ tableLayout: 'auto' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>Source</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>Campaign</th>
                      <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>Visitors</th>
                      <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>Conversions</th>
                      <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {insights.attribution.map((a, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '7px 10px', fontSize: 13 }}>
                          <span className="pill">{a.source}</span>
                          {a.medium && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--muted)' }}>{a.medium}</span>}
                        </td>
                        <td style={{ padding: '7px 10px', fontSize: 13, color: 'var(--muted)' }}>{a.campaign || '—'}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontSize: 13 }}>{a.visitors}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontSize: 13 }}>{a.conversions}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: rateColor(a.rate) }}>{a.rate}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ color: 'var(--muted)', fontSize: 13, paddingTop: 12 }}>
                No campaign traffic yet — visits need a utm_source, gclid, or fbclid link to appear here.
              </div>
            )
          ) : (
            <div style={{ color: 'var(--muted)', fontSize: 13, paddingTop: 12 }}>Loading…</div>
          )}
        </div>

        {/* ── Cart Activity ── */}
        <div className="chart-container" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline"><h3>Cart Activity</h3></div>
          {insights?.cartActivity ? (() => {
            const { added, removed, edited } = insights.cartActivity;
            const retainRate = added > 0 ? Math.round((1 - removed / added) * 100) : null;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
                {[
                  { label: 'Added', value: added, color: '#22c55e' },
                  { label: 'Removed', value: removed, color: '#ef4444' },
                  { label: 'Quantity edits', value: edited, color: '#f59e0b' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: 'var(--bg)', borderRadius: 'var(--r-xl)', padding: '10px 14px', borderLeft: `3px solid ${color}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{label}</span>
                    <span style={{ fontSize: 20, fontWeight: 700, color }}>{value.toLocaleString()}</span>
                  </div>
                ))}
                {retainRate !== null && (
                  <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{retainRate}% of added items stayed in cart (weren't removed)</p>
                )}
              </div>
            );
          })() : (
            <div style={{ color: 'var(--muted)', fontSize: 13, paddingTop: 12 }}>Loading…</div>
          )}
        </div>

        {/* ── Outbound Clicks ── */}
        <div className="chart-container" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline"><h3>Outbound Clicks</h3></div>
          <div className="custom-table-wrapper">
            <table className="stats-table">
              <tbody>
                {(insights?.outboundLinks || []).map((o, i) => (
                  <tr key={i}>
                    <td style={{ maxWidth: 0 }}>
                      <span className="pill">{o.kind}</span>
                      <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--muted)', wordBreak: 'break-all' }}>{o.href}</span>
                    </td>
                    <td align="right"><b>{o.count}</b></td>
                  </tr>
                ))}
                {insights && (!insights.outboundLinks || insights.outboundLinks.length === 0) && (
                  <tr><td colSpan={2} style={{ color: 'var(--muted)', fontSize: 12, padding: '8px 0' }}>No outbound clicks yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Error Log ── */}
        <div className="chart-container col-span-2" onMouseEnter={onEnter} onMouseLeave={onLeave}>
          <div className="panel-header-inline" style={{ justifyContent: 'space-between' }}>
            <h3>Error Log</h3>
            {insights?.errors && (
              <span style={{ fontSize: 12, fontWeight: 700, color: insights.errors.total > 0 ? '#ef4444' : 'var(--muted)' }}>
                {insights.errors.total} in {days}d
              </span>
            )}
          </div>
          {insights?.errors ? (
            insights.errors.recent.length > 0 ? (
              <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {insights.errors.recent.map((e, i) => (
                  <div key={i} style={{ padding: '8px 10px', background: 'var(--bg)', borderRadius: 'var(--r-lg)', borderLeft: '3px solid #ef4444' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{e.message || 'Unknown error'}</span>
                      <span style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{moment(e.ts).fromNow()}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{e.path}{e.source ? ` · ${e.source}` : ''}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: 'var(--muted)', fontSize: 13, paddingTop: 12 }}>No errors recorded — nice.</div>
            )
          ) : (
            <div style={{ color: 'var(--muted)', fontSize: 13, paddingTop: 12 }}>Loading…</div>
          )}
        </div>

      </div>
      </>
    )}
    </div>

  );

};


export default AnalyticsDashboard;